// orbit.ts — Shared Kepler-orbit math: solve the eccentric anomaly and place a
// body in its perifocal plane (the attractor at one focus). Used by planets,
// moons, and the orbit-path renderer so motion and drawn ellipse stay in sync.
import type { Vector3 } from 'three';

const TWO_PI = Math.PI * 2;

export interface OrbitElements {
  semiMajorAxis: number;
  eccentricity: number;
  period: number;
  phase: number;
  longitudeOfPeriapsis: number;
}

// Solve M = E - e*sin(E) for the eccentric anomaly E (Newton-Raphson).
export function eccentricAnomaly(meanAnomaly: number, e: number): number {
  let eAnom = e < 0.8 ? meanAnomaly : Math.PI;
  for (let i = 0; i < 6; i++) {
    eAnom -= (eAnom - e * Math.sin(eAnom) - meanAnomaly) / (1 - e * Math.cos(eAnom));
  }
  return eAnom;
}

// Write the body's position at `time` (seconds) into `out`, in its orbital plane
// (plane normal = +Y), rotated by the argument of periapsis. Speeds up near
// periapsis and slows at apoapsis (Kepler's 2nd law) for free via the solve.
export function orbitPosition(o: OrbitElements, time: number, out: Vector3): Vector3 {
  const m = (o.phase + (TWO_PI / o.period) * time) % TWO_PI;
  const eAnom = eccentricAnomaly(m, o.eccentricity);
  const b = o.semiMajorAxis * Math.sqrt(1 - o.eccentricity * o.eccentricity);
  const xp = o.semiMajorAxis * (Math.cos(eAnom) - o.eccentricity);
  const zp = b * Math.sin(eAnom);
  const cosW = Math.cos(o.longitudeOfPeriapsis);
  const sinW = Math.sin(o.longitudeOfPeriapsis);
  return out.set(xp * cosW - zp * sinW, 0, xp * sinW + zp * cosW);
}
